import React, { useMemo } from 'react';
import {
  Card,
  Box,
  Typography,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
} from '@mui/material';
import { LineChart } from '@mui/x-charts';
import {
  useAxisTooltip,
  ChartsTooltipPaper,
} from '@mui/x-charts/ChartsTooltip';
import { METRIC_LIST } from '../data/publisher.data';
import SoothCard from 'components/base/SoothCard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { colorArray } from '../data/color.data';

// v7 custom axis tooltip (sorted, MUI Table component)


export default function EngagementLeaderboard({
  data,
  isLoading,
  metric,
  metricValue,
  setMetric,
}) {

  function SortedAxisContent() {
    const t = useAxisTooltip();
    if (!t) return null;
  
    const rows = t.seriesItems
      .filter((s) => s.value != null && !Number.isNaN(Number(s.value)))
      .slice()
      .sort((a, b) => metricValue !== 1 ? Number(b.value) - Number(a.value) : Number(a.value) - Number(b.value));
  
    return (
      <ChartsTooltipPaper>
        <Box p={2}>
          {t.axisFormattedValue && (
            <>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {t.axisFormattedValue}
              </Typography>
              <Divider sx={{ mb: 1 }} />
            </>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: 'none', p: 0.5, fontWeight: 600 }}>Publisher</TableCell>
                <TableCell sx={{ border: 'none', p: 0.5, fontWeight: 600, textAlign: 'right' }}>
                  {METRIC_LIST.find(m => m.metricId === metricValue)?.metricName || 'Value'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={`${s.seriesId}`}>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: s.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 0 }}>
                        {s.formattedLabel ?? String(s.seriesId)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ border: 'none', p: 0.5, textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {s.formattedValue ?? String(s.value)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </ChartsTooltipPaper>
    );
  }

  const chartData = useMemo(() => {
    if (!Array.isArray(data)) return { series: [], xLabels: [], maxValue: 0 };

    // Group by publisher
    const publisherGroups = data.reduce((acc, item) => {
      const publisher = item.publisher;
      if (!acc[publisher]) acc[publisher] = [];
      acc[publisher].push({ interval: item.interval, views: Number(item.views) });
      return acc;
    }, {});

    const allIntervals = [...new Set(data.map((item) => item.interval))];

    // Series per publisher aligned to allIntervals
    const series = Object.keys(publisherGroups).map((publisher) => ({
      label: publisher,
      data: allIntervals.map((interval) => {
        const found = publisherGroups[publisher].find((d) => d.interval === interval);
        return found ? Number(found.views) : 0;
      }),
    }));

    const maxValue = Math.max(...data.map((item) => Number(item.views)), 0);

    return { series, xLabels: allIntervals, maxValue };
  }, [data]);

  if (isLoading) {
    return (
      <Card
        sx={{
          backgroundColor: (theme) => theme.palette.secondary.main,
          borderRadius: 2,
          p: 2,
          height: 400,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h4">
            Engagement Leaderboard
          </Typography>
          <Tooltip title="Performance comparison of publishers based on engagement metrics, showing trends over time." placement="top">
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoOutlinedIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        </Box>
        <Skeleton variant="rectangular" width="100%" height={300} />
      </Card>
    );
  }

  return (
    <SoothCard
      sx={{
        backgroundColor: (theme) => theme.palette.secondary.main,
        borderRadius: 2,
        p: 2,
        height: 400,
      }}
    >
      <Stack
        sx={{ flexDirection: { sm: 'column', md: 'row' } }}
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography variant="h4" sx={{ textAlign: 'center' }}>
            Engagement Leaderboard
          </Typography>
          <Tooltip title="Performance comparison of publishers based on engagement metrics, showing trends over time." placement="top">
            <IconButton size="small" sx={{ p: 0.5 }}>
              <InfoOutlinedIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Metric</InputLabel>
            <Select
              value={metricValue}
              label="Metric"
              onChange={(e) => setMetric(e.target.value)}
              size="small"
            >
              {METRIC_LIST.map((m) => (
                <MenuItem key={m.metricId} value={m.metricId}>
                  {m.metricName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>
      <Box sx={{ width: '100%', height: 300 }}>
        <LineChart
          series={chartData.series}
          xAxis={[
            {
              scaleType: 'point',
              data: chartData.xLabels,
              labelRotation: 0,
            },
          ]}
          yAxis={[
            {
              min: 0,
              max: chartData.maxValue,
              reverse: metricValue === 1,
              valueFormatter: (value) => {
                const v = Math.round(Number(value));
                return v === 0 ? '' : String(v);
              },
            },
          ]}
          // v7 tooltip config + custom content
          tooltip={{ trigger: 'axis' }}
          slots={{ axisContent: SortedAxisContent }}
          // legend off, fixed palette
          slotProps={{ legend: { hidden: true } }}
          colors={colorArray}
          sx={{ width: '100%', height: '100%' }}
          height={300}
        />
      </Box>
    </SoothCard>
  );
}
